import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function Checkout() {
  const { items, total } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ shipping_name: "", shipping_address: "", shipping_city: "", shipping_zip: "" });
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    if (user !== false && items.length === 0) nav("/shop");
  }, [items.length, user, nav]);

  if (user === false) {
    return (
      <div className="max-w-md mx-auto px-5 py-24 text-center" data-testid="checkout-auth-required">
        <h1 className="font-anton text-4xl uppercase tracking-tighter">Sign In To Checkout</h1>
        <p className="font-mono text-sm text-muted-foreground mt-3">You need an account to place an order.</p>
        <Link to="/login" className="inline-block mt-6 bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm px-8 py-4 border-2 border-border hover:bg-secondary hover:border-secondary hover:text-white transition-colors">Sign In</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  const place = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const payload = {
        ...form,
        origin_url: window.location.origin,
        items: items.map((i) => ({
          product_id: i.product_id, qty: i.qty, size: i.size || null, color: i.color || null,
        })),
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data.url) {
        window.location.href = data.url; // redirect to Stripe-hosted checkout
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Checkout failed");
      setPlacing(false);
    }
  };

  const inputCls = "w-full border-2 border-border px-4 py-3 font-mono text-sm focus:outline-none focus:border-primary";

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-12 md:py-16" data-testid="checkout-page">
      <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-tighter mb-10">Checkout</h1>
      <form onSubmit={place} className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="border-2 border-border p-6">
            <h2 className="font-outfit font-bold uppercase tracking-widest text-sm mb-5">Shipping Details</h2>
            <div className="grid gap-4">
              <input required placeholder="FULL NAME" data-testid="ship-name" value={form.shipping_name} onChange={(e) => setForm({ ...form, shipping_name: e.target.value })} className={inputCls} />
              <input required placeholder="STREET ADDRESS" data-testid="ship-address" value={form.shipping_address} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} className={inputCls} />
              <div className="grid grid-cols-2 gap-4">
                <input required placeholder="CITY" data-testid="ship-city" value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} className={inputCls} />
                <input required placeholder="ZIP CODE" data-testid="ship-zip" value={form.shipping_zip} onChange={(e) => setForm({ ...form, shipping_zip: e.target.value })} className={inputCls} />
              </div>
            </div>
          </div>
          <div className="border-2 border-border p-6 bg-muted">
            <div className="flex items-center gap-3">
              <Lock size={18} />
              <div>
                <h2 className="font-outfit font-bold uppercase tracking-widest text-sm">Secure Payment via Stripe</h2>
                <p className="font-mono text-xs text-muted-foreground mt-1">You'll be redirected to Stripe to complete your card payment safely.</p>
              </div>
            </div>
          </div>
        </div>
        <div className="border-2 border-border p-6 h-fit">
          <h2 className="font-outfit font-bold uppercase tracking-widest text-sm mb-5">Summary</h2>
          <div className="space-y-3 mb-5 max-h-60 overflow-auto">
            {items.map((it, i) => (
              <div key={i} className="flex justify-between font-mono text-xs">
                <span className="truncate pr-2">{it.qty}× {it.name}</span>
                <span>${(it.price * it.qty).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-anton text-2xl border-t-2 border-border pt-4">
            <span>Total</span><span>${total.toFixed(2)}</span>
          </div>
          <button type="submit" disabled={placing} data-testid="place-order-btn" className="mt-6 w-full bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm py-4 border-2 border-primary hover:bg-secondary hover:border-secondary transition-colors disabled:opacity-50">
            {placing ? "Redirecting..." : "Pay With Stripe"}
          </button>
        </div>
      </form>
    </div>
  );
}
